import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { fmtWeekRange } from "@/lib/dates";
import type { PlanWeekOverview } from "@/types";

const PHASE_DOT: Record<string, string> = {
  "Re-entry": "bg-amber-light",
  "Base 1": "bg-amber",
  "Base 2": "bg-amber-dark",
  "Build 1": "bg-navy-soft",
  "Build 2": "bg-navy",
  Peak: "bg-coral",
  Taper: "bg-taupe",
  "Post-race": "bg-taupe-light",
};

interface Props {
  week: PlanWeekOverview;
  isCurrent?: boolean;
}

export function WeekCard({ week, isCurrent = false }: Props) {
  const { t } = useTranslation();
  return (
    <Link to={`/plan/week/${week.week_number}`}>
      <Card
        className={
          isCurrent
            ? "flex items-center gap-4 border-amber/50 ring-1 ring-amber/30"
            : "flex items-center gap-4 hover:border-amber/40"
        }
      >
        <div className="flex w-12 shrink-0 flex-col items-center">
          <span className="text-[10px] uppercase tracking-wider text-taupe">{t("plan.wk_short")}</span>
          <span className="metric-number text-[20px]">{week.week_number}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${PHASE_DOT[week.phase] || "bg-taupe"}`} />
            <span className="font-medium text-navy-deep">{week.phase}</span>
            {week.provisional ? <span className="text-[11px] text-amber-dark">{t("planning_quality.preview_title")}</span> : null}
            {isCurrent ? (
              <span className="rounded-full bg-amber/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-dark">
                сейчас
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 text-[13px] text-taupe">
            {fmtWeekRange(week.week_start_date)} · TSS {week.target_tss}
            {week.target_ctl != null ? ` · CTL ${Math.round(week.target_ctl)}` : ""}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-taupe-light" strokeWidth={1.6} />
      </Card>
    </Link>
  );
}
